import { Text } from "ui/Typography"
import styles from "../index.module.css"
import moment from "moment"
import PropTypes from "prop-types"
import { getJournalIcon } from "pagesV2/pro/Calendar/utils"
import IconWithBg from "ui/IconWithBg"
import { useMemo, useState } from "react"
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails"
import { Flex } from "antd"
import { useTimeOffByDay } from "pagesV2/pro/Calendar/hooks/useTimeOff"
import Journal from "pagesV2/pro/Calendar/Modals/Journal"
import RenderEventBlocks from "./RenderEventBlocks"
import { useDevice } from "hooks/useDevice"
import useActiveGuide from "hooks/useActiveClient"

const WeekGrid = ({ calendarDays, hours, events, journalNoteDates = [], onJournalClick }) => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const { isMobile, isTablet } = useDevice()
  const isCompact = isMobile || isTablet
  const [active] = useActiveGuide()

  const weekStartDate = calendarDays?.[0]?.format("YYYY-MM-DD")
  const weekEndDate = calendarDays?.at(-1)?.format("YYYY-MM-DD")

  // Use external callback if provided (e.g., from crew member calendar)
  // Otherwise use internal modal for main calendar
  const useExternalJournalHandler = Boolean(onJournalClick)

  const normalizedEventsByDay = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      const dayStr = day.format("YYYY-MM-DD")
      acc[dayStr] = events
        .filter((ev) => moment(ev.date).isSame(day, "day"))
        .map((ev) => {
          const startMoment = ev.start ? moment(ev.start, ["h:mm A", "hh:mm A", "HH:mm"]) : null
          const endMoment = ev.end ? moment(ev.end, ["h:mm A", "hh:mm A", "HH:mm"]) : null
          return {
            ...ev,
            startMoment,
            endMoment,
            startHour: startMoment?.isValid() ? startMoment.hour() : null,
            endHour: endMoment?.isValid() ? endMoment.hour() : null,
            startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
            endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end
          }
        })
      return acc
    }, {})
  }, [calendarDays, events])
  // Normalize time off per day (centralized hook)
  const timeOffByDay = useTimeOffByDay(calendarDays)
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false)

  const handleJournalModalOpen = (date) => {
    if (useExternalJournalHandler) {
      // Use external callback (e.g., crew member calendar with context)
      onJournalClick(date)
    } else {
      // Use internal modal (main guide calendar)
      setIsJournalModalOpen(true)
      setSelectedDate(date)
    }
  }
  const handleCancel = () => {
    setIsJournalModalOpen(false)
  }

  const handleBookingCancel = () => {
    setIsBookingModalOpen(false)
    setSelectedBookingId(null)
  }
  return (
    <div
      className={`${styles.timeGridTable} ${styles.weekGrid}`}
      style={{
        gridTemplateRows: `repeat(${hours.length + 1}, 80px)`
      }}
    >
      <div className={styles.timeGridHeaderCell}></div>
      {calendarDays.map((day) => {
        const dayStr = day.format("YYYY-MM-DD")
        const dayLabel = isCompact ? day.format("dd")[0] : day.format("ddd")
        return (
          <Flex key={dayStr} gap={5} className={`py-s px-xs ${styles.timeGridHeaderCell}`} align="center" justify="center">
            <Flex vertical justify={isCompact ? "space-between" : "center"} className={`${isCompact ? "h-full" : ""} ml-auto`}>
              <Text fw="500">{dayLabel}</Text>
              <Text fw="500">{day.format("D")}</Text>
            </Flex>
            {!isCompact && (
              <Flex className="ml-auto" align="center">
                <IconWithBg
                  disabled={!active}
                  icon={getJournalIcon(journalNoteDates, dayStr)}
                  onClick={active ? () => handleJournalModalOpen(dayStr) : undefined}
                />
              </Flex>
            )}
          </Flex>
        )
      })}
      {/* Time + event rows */}
      {hours.map((hour) => [
        // Time label cell (first column)
        <div key={`label-${hour}`} className={styles.timeLabelCell}>
          <Text fw="500" className="py-m">
            {moment(hour, "H").format("h A")}
          </Text>
        </div>,
        // Day cells for this hour
        ...calendarDays.map((day) => {
          const dayStr = day.format("YYYY-MM-DD")
          const cellEvents =
            normalizedEventsByDay[dayStr]?.filter(
              (ev) => ev.startHour !== null && ev.endHour !== null && ev.startHour <= hour && ev.endHour > hour
            ) || []
          const timeOffForDay = timeOffByDay[dayStr]
          return (
            <div key={dayStr + hour} className={styles.eventCell}>
              {(timeOffForDay?.ranges || []).map((toff) => {
                const start = toff.start.hour() + toff.start.minute() / 60
                const end = toff.end.hour() + toff.end.minute() / 60
                if (start < hour + 1 && end > hour) {
                  const top = Math.max(0, (start - hour) * 100) // percent within the hour cell
                  const height = (Math.min(end, hour + 1) - Math.max(start, hour)) * 100
                  return (
                    <div
                      key={`${dayStr}-${toff.start.format("HH:mm")}-${toff.end.format("HH:mm")}`}
                      className={styles.timeOffOverlay}
                      style={{ top: `${top}%`, height: `${height}%` }}
                    />
                  )
                }
                return null
              })}
              <RenderEventBlocks
                day={day}
                hour={hour}
                cellEvents={cellEvents}
                timeOffForDay={timeOffForDay}
                setIsBookingModalOpen={setIsBookingModalOpen}
                setSelectedBookingId={setSelectedBookingId}
              />
            </div>
          )
        })
      ])}
      {!useExternalJournalHandler && isJournalModalOpen && (
        <Journal
          open={isJournalModalOpen}
          setOpen={setIsJournalModalOpen}
          onCancel={handleCancel}
          selectedDate={selectedDate}
          startDate={weekStartDate}
          endDate={weekEndDate}
        />
      )}
      {isBookingModalOpen && (
        <BookingDetailsModal open={isBookingModalOpen} handleOnCancel={handleBookingCancel} bookingUuid={selectedBookingId} />
      )}
    </div>
  )
}

WeekGrid.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  hours: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
  journalNoteDates: PropTypes.array,
  onJournalClick: PropTypes.func
}

export default WeekGrid
